import { Grid, makeStyles, Tooltip } from "@material-ui/core";
import { HISTORY_TAB, IMAGE_TREATMENT_TAB, SENSITIVE_PLACEHOLDER_IMAGE } from "app/common/LocalConst";
import { getDateTime } from "app/common/LocalFunction";
import { getLatestResultImagesIn3DiagnosticReportsByPatientId } from "app/services/DiagnosticReportImageResultService";
import { getFileById } from "app/services/FileDescriptionExtService";
import "lightgallery/css/lg-fullscreen.css";
import "lightgallery/css/lg-rotate.css";
import "lightgallery/css/lg-thumbnail.css";
import "lightgallery/css/lg-zoom.css";
import "lightgallery/css/lightgallery.css";
import lgFullscreen from "lightgallery/plugins/fullscreen";
import lgRotate from "lightgallery/plugins/rotate";
import lgThumbnail from "lightgallery/plugins/thumbnail";
import lgZoom from "lightgallery/plugins/zoom";
import LightGallery from "lightgallery/react";
import { memo, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";
import { toast } from "react-toastify";

const useStyles = makeStyles((theme) => ({
  container: {
    padding: theme.spacing(2),
  },
  gridContainer: {
    "& .lightgallery-wrapper": {
      display: "grid",
      gap: theme.spacing(2),
      gridTemplateColumns: "repeat(1, 1fr)",
      [theme.breakpoints.up("sm")]: {
        gridTemplateColumns: "repeat(3, 1fr)",
      },
      [theme.breakpoints.up("md")]: {
        gridTemplateColumns: "repeat(4, 1fr)",
      },
      [theme.breakpoints.up("xl")]: {
        gridTemplateColumns: "repeat(6, 1fr)",
      },
    },
  },
  imageItem: {
    display: "block",
    textDecoration: "none",
    border: "1px solid #ccc",
    borderRadius: "5px",
    padding: theme.spacing(1),
  },
  galleryImage: {
    width: "100%",
    height: "180px",
    objectFit: "cover",
    borderRadius: "5px",
    border: "1px solid #eee",
    cursor: "pointer",
    transition: "transform 0.2s ease-in-out",
    "&:hover": {
      transform: "scale(1.02)",
    },
  },
}));

const ListRecentDiagnosticReportImages = ({ setCurrentTab }) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const [reportImages, setReportImages] = useState([]);

  const urlParams = new URLSearchParams(window.location.search);
  const patientId = urlParams.get("patientId");

  useEffect(() => {
    if (patientId) {
      fetchData();
    }
  }, [patientId]);

  const fetchData = async () => {
    try {
      const { data } =
        await getLatestResultImagesIn3DiagnosticReportsByPatientId(patientId);
      const imageObjects = await convertFileDescriptionToImageObjects(data);
      setReportImages(imageObjects);
    } catch (error) {
      console.error(error);
      toast.error("Có lỗi xảy ra khi lấy dữ liệu hình ảnh điều trị gần đây");
    }
  };

  const convertFileDescriptionToImageObjects = async (data) => {
    const items = Array.from(data ?? []);
    const galleryItems = await Promise.all(
      items.map(async (item) => {
        if (item?.isSensitive) {
          return {
            data: item,
            src: SENSITIVE_PLACEHOLDER_IMAGE,
            //alt: "Hình ảnh nhạy cảm",
            isSensitive: true
          };
        }

        const fileId = item?.fileDescription?.id;
        if (!fileId) return null; // Prevent API call if no ID
        try {
          const { data: fileData } = await getFileById(fileId);
          const objectURL = URL.createObjectURL(fileData);
          return {
            data: item,
            src: objectURL,
            alt: item?.fileDescription?.name,
          };
        } catch (error) {
          console.warn(`Failed to fetch file for id ${fileId}`, error);
          return null;
        }
      })
    );
    return galleryItems.filter(Boolean);
  };

  const location = useLocation();
  const isInDetailPage =
    location.pathname === "/treatment-management/treatment-patient-info-detail";
  return (
    <div
      style={{
        backgroundColor: "aliceblue",
        boxShadow: "0 8px 24px rgba(0, 0, 0, 0.08)",
      }}
    >
      <Grid container spacing={1} className="m-0">
        <Grid
          item
          xs={12}
          className="search-title-card"
          style={{ maxWidth: "99%" }}
        >
          <p className="title-block">Hình ảnh kết quả điều trị gần đây</p>
          {isInDetailPage ? (
            <div className="flex align-center flex-end cursor">
              <p
                className="button-more"
                onClick={() => setCurrentTab(HISTORY_TAB)}
              >
                Xem thêm
              </p>
            </div>
          ) : (
            <div className="flex align-center flex-end gap-1 cursor">
              <p
                className="button-more"
                onClick={() => setCurrentTab(IMAGE_TREATMENT_TAB)}
              >
                Xem thêm
              </p>
            </div>
          )}
        </Grid>

        <Grid item xs={12}>
          {reportImages.length > 0 ? (
            <div className={classes.gridContainer}>
              <LightGallery
                speed={500}
                plugins={[lgThumbnail, lgZoom, lgFullscreen, lgRotate]}
                mode="lg-fade"
                elementClassNames="lightgallery-wrapper"
              >
                {reportImages.map((el, index) => (
                  <a
                    key={index}
                    className={classes.imageItem}
                    href={el?.src}
                    data-src={el?.src}
                    data-sub-html={el?.alt || ""}
                  >
                    <Grid container spacing={1}>
                      <Grid item xs={12} className="flex justify-center">
                        <span>
                          Ngày đăng:{" "}
                          <span>
                            {el?.data?.createDate
                              ? getDateTime(el?.data?.createDate)
                              : ""}
                          </span>
                        </span>
                      </Grid>
                      <Grid item xs={12}>
                        {(el?.isSensitive) ?
                          <img
                            src={el?.src}
                            alt={el?.alt}
                            className={classes.galleryImage}
                            title="Ảnh nhạy cảm – cần quyền để xem"
                          />
                          :
                          <img
                            src={el?.src}
                            alt={el?.alt}
                            className={classes.galleryImage}
                          />}
                      </Grid>
                      <Grid item xs={12} className="flex justify-center">
                        <span>{el?.data?.digImageDescription}</span>
                      </Grid>
                    </Grid>
                  </a>
                ))}
              </LightGallery>
            </div>
          ) : (
            <div className="flex justify-center items-center w-full mt-5">
              Chưa có dữ liệu
            </div>
          )}
        </Grid>
      </Grid>
    </div>
  );
};

export default memo(ListRecentDiagnosticReportImages);
